<template>
  <v-card 
    class="map-controls-panel" 
    elevation="8"
    :style="{ 
      position: 'absolute', 
      top: '20px', 
      right: '20px', 
      zIndex: 1000,
      width: isPanelExpanded ? '420px' : '60px',
      transition: 'all 0.3s ease'
    }"
  >
    <!-- Toggle Button -->
    <v-btn
      icon
      size="large"
      class="control-toggle-btn"
      @click="togglePanel"
      :style="{
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 1001
      }"
    >
      <v-icon>{{ isPanelExpanded ? 'mdi-close' : 'mdi-tune' }}</v-icon>
    </v-btn>

    <!-- Expanded Controls Panel -->
    <v-slide-x-transition>
      <div v-if="isPanelExpanded" class="controls-content pa-4">
        <!-- Header -->
        <div class="mb-4">
          <h3 class="text-h6 font-weight-bold d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-tune-variant</v-icon>
            Controles del Mapa
          </h3>
          <v-divider class="mt-2"></v-divider>
        </div>

        <!-- Layer Visibility Controls -->
        <div class="mb-4">
          <h4 class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center">
            <v-icon class="mr-2" size="small">mdi-layers</v-icon>
            Capas de Visualización
          </h4>
          
          <v-row dense>
            <v-col cols="6" v-for="(layer, key) in layerConfig" :key="key">
              <v-switch
                v-model="layerVisibility[key]"
                :label="layer.label"
                :color="layer.color"
                density="compact"
                hide-details
                @change="handleLayerToggle(key, layerVisibility[key])"
              >
                <template v-slot:prepend>
                  <v-icon :color="layer.color" size="small">{{ layer.icon }}</v-icon>
                </template>
              </v-switch>
            </v-col>
          </v-row>

          <!-- Clustering Control -->
          <v-row dense class="mt-2">
            <v-col cols="12">
              <v-switch
                v-model="layerVisibility.clustering"
                label="Agrupación Inteligente"
                color="purple"
                density="compact"
                hide-details
                @change="handleClusteringToggle"
              >
                <template v-slot:prepend>
                  <v-icon color="purple" size="small">mdi-group</v-icon>
                </template>
              </v-switch>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Advanced Filters -->
        <div class="mb-4">
          <h4 class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center">
            <v-icon class="mr-2" size="small">mdi-filter-variant</v-icon>
            Filtros Avanzados
          </h4>

          <!-- Date Range Filter -->
          <v-row dense class="mb-3">
            <v-col cols="12">
              <v-text-field
                v-model="filters.dateRange"
                label="Rango de Fechas"
                type="date"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="handleDateFilter"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-calendar</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Priority Filter -->
          <v-row dense class="mb-3">
            <v-col cols="12">
              <v-select
                v-model="filters.priority"
                :items="priorityOptions"
                label="Prioridad de Incidencias"
                multiple
                chips
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="handlePriorityFilter"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-alert</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Status Filter -->
          <v-row dense class="mb-3">
            <v-col cols="12">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                label="Estado de Entidades"
                multiple
                chips
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="handleStatusFilter"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-information</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Zone Filter -->
          <v-row dense class="mb-3">
            <v-col cols="12">
              <v-select
                v-model="filters.zone"
                :items="zoneOptions"
                label="Zona Geográfica"
                multiple
                chips
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="handleZoneFilter"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-map</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Geographic Search -->
        <div class="mb-4">
          <h4 class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center">
            <v-icon class="mr-2" size="small">mdi-magnify</v-icon>
            Búsqueda Geográfica
          </h4>

          <!-- Address Search -->
          <v-row dense class="mb-2">
            <v-col cols="12">
              <v-autocomplete
                v-model="searchQuery.address"
                :items="addressSuggestions"
                :loading="isSearchLoading"
                label="Buscar por dirección"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                @update:search="handleAddressSearch"
                @update:model-value="handleAddressSelect"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-home-map-marker</v-icon>
                </template>
                <template v-slot:append-inner>
                  <v-btn
                    icon
                    size="x-small"
                    @click="centerOnAddress"
                    :disabled="!searchQuery.address"
                  >
                    <v-icon size="small">mdi-crosshairs-gps</v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Agency Search -->
          <v-row dense class="mb-2">
            <v-col cols="12">
              <v-autocomplete
                v-model="searchQuery.agency"
                :items="agencySuggestions"
                label="Buscar por agencia"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                @update:model-value="handleAgencySelect"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-office-building</v-icon>
                </template>
                <template v-slot:append-inner>
                  <v-btn
                    icon
                    size="x-small"
                    @click="centerOnAgency"
                    :disabled="!searchQuery.agency"
                  >
                    <v-icon size="small">mdi-crosshairs-gps</v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Quick Actions -->
          <v-row dense>
            <v-col cols="6">
              <v-btn
                block
                size="small"
                variant="outlined"
                color="primary"
                @click="centerOnCurrentLocation"
                :loading="isGettingLocation"
              >
                <v-icon size="small" class="mr-1">mdi-crosshairs</v-icon>
                Mi Ubicación
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                size="small"
                variant="outlined"
                color="secondary"
                @click="resetMapView"
              >
                <v-icon size="small" class="mr-1">mdi-home</v-icon>
                Vista General
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Quick Focus Buttons -->
        <div class="mb-4">
          <h4 class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center">
            <v-icon class="mr-2" size="small">mdi-target</v-icon>
            Enfoque Rápido
          </h4>
          
          <v-row dense>
            <v-col cols="4">
              <v-btn
                block
                size="small"
                variant="tonal"
                color="red"
                @click="focusOnIncidents"
              >
                <v-icon size="small" class="mr-1">mdi-alert-circle</v-icon>
                Incidencias
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                size="small"
                variant="tonal"
                color="blue"
                @click="focusOnSupervisors"
              >
                <v-icon size="small" class="mr-1">mdi-account-supervisor</v-icon>
                Supervisores
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                size="small"
                variant="tonal"
                color="green"
                @click="focusOnAgencies"
              >
                <v-icon size="small" class="mr-1">mdi-store</v-icon>
                Agencias
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Action Buttons -->
        <div>
          <v-row dense>
            <v-col cols="6">
              <v-btn
                block
                size="small"
                color="warning"
                variant="tonal"
                @click="clearAllFilters"
              >
                <v-icon size="small" class="mr-1">mdi-filter-off</v-icon>
                Limpiar Filtros
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                size="small"
                color="success"
                variant="tonal"
                @click="refreshData"
                :loading="isRefreshing"
              >
                <v-icon size="small" class="mr-1">mdi-refresh</v-icon>
                Actualizar
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-slide-x-transition>

    <!-- Collapsed State Indicators -->
    <div v-if="!isPanelExpanded" class="collapsed-indicators pa-2">
      <!-- Active Filters Indicator -->
      <v-badge
        v-if="activeFiltersCount > 0"
        :content="activeFiltersCount"
        color="error"
        class="mb-2"
      >
        <v-icon color="primary">mdi-filter</v-icon>
      </v-badge>
      
      <!-- Active Layers Indicator -->
      <v-badge
        :content="activeLayersCount"
        color="success"
      >
        <v-icon color="primary">mdi-layers</v-icon>
      </v-badge>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useMapLayers } from '../composables/useMapLayers'
import { useMapData } from '../composables/useMapData'

// Props and Emits
interface Props {
  map?: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  layerToggle: [layer: string, visible: boolean]
  filterChange: [filters: any]
  centerMap: [coordinates: [number, number], zoom?: number]
  focusOn: [layerType: string]
  refresh: []
}>()

// Composables
const { layerVisibility, layerFilters, updateFilters } = useMapLayers()
const { refreshData: refreshMapData } = useMapData()

// State
const isPanelExpanded = ref(false)
const isSearchLoading = ref(false)
const isGettingLocation = ref(false)
const isRefreshing = ref(false)

// Search state
const searchQuery = ref({
  address: null as string | null,
  agency: null as string | null
})

// Filter state
const filters = ref({
  dateRange: '',
  priority: [] as string[],
  status: [] as string[],
  zone: [] as string[]
})

// Configuration
const layerConfig = {
  incidents: {
    label: 'Incidencias',
    icon: 'mdi-alert-circle',
    color: 'red'
  },
  supervisors: {
    label: 'Supervisores',
    icon: 'mdi-account-supervisor',
    color: 'blue'
  },
  agencies: {
    label: 'Agencias',
    icon: 'mdi-office-building',
    color: 'green'
  },
  routes: {
    label: 'Rutas',
    icon: 'mdi-routes',
    color: 'purple'
  }
}

// Options for filters
const priorityOptions = [
  { title: 'Crítica', value: 'CRITICA' },
  { title: 'Alta', value: 'ALTA' },
  { title: 'Media', value: 'MEDIA' },
  { title: 'Baja', value: 'BAJA' }
]

const statusOptions = [
  { title: 'Activo', value: 'ACTIVO' },
  { title: 'Pendiente', value: 'PENDIENTE' },
  { title: 'En Proceso', value: 'EN_PROCESO' },
  { title: 'Resuelto', value: 'RESUELTA' },
  { title: 'Ocupado', value: 'OCUPADO' },
  { title: 'Descanso', value: 'DESCANSO' }
]

const zoneOptions = [
  { title: 'Distrito Nacional', value: 'DN' },
  { title: 'Santo Domingo Norte', value: 'SDN' },
  { title: 'Santo Domingo Este', value: 'SDE' },
  { title: 'Santo Domingo Oeste', value: 'SDO' },
  { title: 'Boca Chica', value: 'BC' },
  { title: 'Los Alcarrizos', value: 'LA' }
]

// Mock suggestions (en un entorno real, estos vendrían de APIs)
const addressSuggestions = ref([
  'Av. 27 de Febrero, Santo Domingo',
  'Malecón, Distrito Nacional',
  'Av. Winston Churchill, Ensanche Piantini',
  'Calle El Conde, Zona Colonial',
  'Av. Máximo Gómez, Gazcue'
])

const agencySuggestions = ref([
  'Policía Nacional - Central',
  'Bomberos - Distrito Nacional',
  'AMET - Zona Colonial',
  'Defensa Civil - Santo Domingo',
  'Cruz Roja - Ensanche Naco'
])

// Computed properties
const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value.dateRange) count++
  if (filters.value.priority.length > 0) count++
  if (filters.value.status.length > 0) count++
  if (filters.value.zone.length > 0) count++
  return count
})

const activeLayersCount = computed(() => {
  return Object.values(layerVisibility.value).filter(Boolean).length
})

// Methods
const togglePanel = () => {
  isPanelExpanded.value = !isPanelExpanded.value
}

const handleLayerToggle = (layer: string, visible: boolean) => {
  console.log('🔧 MapControls - Toggle', layer, 'visible:', visible)
  console.log('🔧 Estado actual layerVisibility:', layerVisibility.value)
  emit('layerToggle', layer, visible)
}

const handleClusteringToggle = () => {
  emit('layerToggle', 'clustering', layerVisibility.value.clustering)
}

const handleDateFilter = (date: string) => {
  // Aplicar filtro de fecha a través del evento
  emit('filterChange', { ...layerFilters.value, dateRange: date })
}

const handlePriorityFilter = (priorities: string[]) => {
  const updatedFilters = {
    ...layerFilters.value,
    incidents: {
      ...layerFilters.value.incidents,
      priorities: priorities
    }
  }
  emit('filterChange', updatedFilters)
}

const handleStatusFilter = (statuses: string[]) => {
  const updatedFilters = {
    ...layerFilters.value,
    incidents: {
      ...layerFilters.value.incidents,
      statuses: statuses
    },
    supervisors: {
      ...layerFilters.value.supervisors,
      statuses: statuses
    }
  }
  emit('filterChange', updatedFilters)
}

const handleZoneFilter = (zones: string[]) => {
  const updatedFilters = {
    ...layerFilters.value,
    supervisors: {
      ...layerFilters.value.supervisors,
      zones: zones
    },
    agencies: {
      ...layerFilters.value.agencies,
      zones: zones
    }
  }
  emit('filterChange', updatedFilters)
}

const handleAddressSearch = async (query: string) => {
  if (!query || query.length < 3) return
  
  isSearchLoading.value = true
  // En un entorno real, aquí haríamos una llamada a una API de geocodificación
  setTimeout(() => {
    isSearchLoading.value = false
  }, 500)
}

const handleAddressSelect = (address: string) => {
  if (address) {
    centerOnAddress()
  }
}

const handleAgencySelect = (agency: string) => {
  if (agency) {
    centerOnAgency()
  }
}

const centerOnAddress = () => {
  if (!searchQuery.value.address) return
  
  // Coordenadas de ejemplo para direcciones de Santo Domingo
  const coordinates: Record<string, [number, number]> = {
    'Av. 27 de Febrero, Santo Domingo': [18.4801, -69.9420],
    'Malecón, Distrito Nacional': [18.4624, -69.9090],
    'Av. Winston Churchill, Ensanche Piantini': [18.4678, -69.9408],
    'Calle El Conde, Zona Colonial': [18.4735, -69.8849],
    'Av. Máximo Gómez, Gazcue': [18.4686, -69.9137]
  }
  
  const coords = coordinates[searchQuery.value.address]
  if (coords) {
    emit('centerMap', coords, 16)
  }
}

const centerOnAgency = () => {
  if (!searchQuery.value.agency) return
  
  // Coordenadas de ejemplo para agencias
  const coordinates: Record<string, [number, number]> = {
    'Policía Nacional - Central': [18.4735, -69.8849],
    'Bomberos - Distrito Nacional': [18.4686, -69.9137],
    'AMET - Zona Colonial': [18.4624, -69.9090],
    'Defensa Civil - Santo Domingo': [18.4801, -69.9420],
    'Cruz Roja - Ensanche Naco': [18.4678, -69.9408]
  }
  
  const coords = coordinates[searchQuery.value.agency]
  if (coords) {
    emit('centerMap', coords, 17)
  }
}

const centerOnCurrentLocation = () => {
  if (!navigator.geolocation) return
  
  isGettingLocation.value = true
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const coords: [number, number] = [
        position.coords.latitude,
        position.coords.longitude
      ]
      emit('centerMap', coords, 15)
      isGettingLocation.value = false
    },
    (error) => {
      console.error('Error getting location:', error)
      isGettingLocation.value = false
      // Fallback a Santo Domingo
      emit('centerMap', [18.7357, -70.1627], 10)
    }
  )
}

const resetMapView = () => {
  // Volver a la vista general de República Dominicana
  emit('centerMap', [18.7357, -70.1627], 8)
}

const clearAllFilters = () => {
  filters.value = {
    dateRange: '',
    priority: [],
    status: [],
    zone: []
  }
  
  // Reset filters and emit event
  const resetFilters = {
    incidents: {
      priorities: [],
      statuses: [],
      categories: []
    },
    supervisors: {
      statuses: [],
      zones: []
    },
    agencies: {
      statuses: [],
      zones: []
    },
    routes: {
      statuses: []
    }
  }
  
  emit('filterChange', resetFilters)
}

const refreshData = async () => {
  isRefreshing.value = true
  try {
    await refreshMapData()
    emit('refresh')
  } finally {
    isRefreshing.value = false
  }
}

// Focus Methods
const focusOnIncidents = () => {
  emit('focusOn', 'incidents')
}

const focusOnSupervisors = () => {
  emit('focusOn', 'supervisors')
}

const focusOnAgencies = () => {
  emit('focusOn', 'agencies')
}

// Initialize
onMounted(() => {
  console.log('MapControls component mounted')
})
</script>

<style scoped>
.map-controls-panel {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2) !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98) !important;
  overflow: hidden;
}

.control-toggle-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.controls-content {
  max-height: 80vh;
  overflow-y: auto;
}

.collapsed-indicators {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

/* Custom scrollbar */
.controls-content::-webkit-scrollbar {
  width: 6px;
}

.controls-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.controls-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.controls-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Smooth transitions */
.v-btn {
  transition: all 0.3s ease !important;
}

.v-switch {
  transition: all 0.2s ease !important;
}
</style>
